import { useMemo, useState } from 'react';
import { RELEASE_APPS, findApp } from '../releaseNotes/manifest';
import {
    countMatches,
    formatReleaseDate,
    normaliseQuery,
    resolveSelectedVersion,
    scoreNotes,
    splitOnQuery,
} from './releaseNotesView';
import { currentDeepLink, writeDeepLink } from './deepLink';

// The Release Notes tab.
//
// MW asked for the Release Notes page they already have in the service tool, for these two apps.
// The shape is the one they know: one release open at a time, picked from a version list, with a
// single search across every note. What is different here is that this dashboard is the front end
// for TWO apps, so the page carries an app switcher and each app keeps its own version stream, its
// own dates and its own newest release.
//
// The notes are bundled (see ../releaseNotes/manifest.js), so this tab makes no request, no CRM
// call and needs no server route. It renders with the API down.
//
// Which version is open is deliberately NOT in the URL, matching the service tool. The app is, so a
// mail can link to ?tab=release-notes&app=calendar, but a version deep link nobody has asked for
// would be a third thing to validate on a page that is one click from anywhere.

const SEARCH_LABEL = 'Search every release of the selected app';

// Render inline **bold** and `code` spans, marking the active query inside the plain runs.
function inline(text, query) {
    return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{highlight(part.slice(2, -2), query, i)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
            return <code key={i} className="release-notes-code">{part.slice(1, -1)}</code>;
        }
        return <span key={i}>{highlight(part, query, i)}</span>;
    });
}

// Wrap each occurrence of the query in a <mark>.
//
// Splitting into data first (splitOnQuery) and building elements here keeps the renderer free of
// dangerouslySetInnerHTML: the notes are ours, but a highlighter that assembled markup from a
// string the reader typed would be injecting that string into the DOM.
function highlight(text, query, keyBase) {
    if (!normaliseQuery(query) || !text) return text;
    return splitOnQuery(text, query).map((part, i) => (
        part.hit
            ? <mark key={`${keyBase}-${i}`} className="release-notes-mark">{part.text}</mark>
            : part.text
    ));
}

// Minimal, dependency-free markdown renderer: ## and ### headings, bullet and numbered lists,
// blank-line paragraphs, **bold** and `code`. A release note needs nothing else, and a markdown
// library for nine short notes would be the largest thing in the bundle.
//
// A leading "# " line is dropped: the header above already names the version.
function Markdown({ source, query }) {
    const blocks = [];
    let list = [];
    let ordered = [];
    let para = [];
    let key = 0;

    const flushPara = () => {
        if (para.length) {
            blocks.push(<p key={key++} className="release-notes-p">{inline(para.join(' '), query)}</p>);
            para = [];
        }
    };
    const flushList = () => {
        if (list.length) {
            const items = list;
            blocks.push(
                <ul key={key++} className="release-notes-list">
                    {items.map((item, i) => <li key={i}>{inline(item, query)}</li>)}
                </ul>
            );
            list = [];
        }
    };
    const flushOrdered = () => {
        if (ordered.length) {
            const items = ordered;
            blocks.push(
                <ol key={key++} className="release-notes-list">
                    {items.map((item, i) => <li key={i}>{inline(item, query)}</li>)}
                </ol>
            );
            ordered = [];
        }
    };

    for (const raw of String(source || '').split('\n')) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            flushPara();
            flushList();
            flushOrdered();
        } else if (line.startsWith('### ')) {
            flushPara();
            flushList();
            flushOrdered();
            blocks.push(<h5 key={key++} className="release-notes-h3">{inline(line.slice(4), query)}</h5>);
        } else if (line.startsWith('## ')) {
            flushPara();
            flushList();
            flushOrdered();
            blocks.push(<h4 key={key++} className="release-notes-h2">{inline(line.slice(3), query)}</h4>);
        } else if (line.startsWith('# ')) {
            flushPara();
            flushList();
            flushOrdered();
        } else if (/^[-*]\s+/.test(line)) {
            flushPara();
            flushOrdered();
            list.push(line.replace(/^[-*]\s+/, ''));
        } else if (/^\d+\.\s+/.test(line)) {
            flushPara();
            flushList();
            ordered.push(line.replace(/^\d+\.\s+/, ''));
        } else {
            para.push(line.trim());
        }
    }
    flushPara();
    flushList();
    flushOrdered();

    return <div className="release-notes-body">{blocks}</div>;
}

function matchLabel(count) {
    return count === 1 ? '1 match' : `${count} matches`;
}

export default function ReleaseNotes() {
    const [appKey, setAppKey] = useState(() => currentDeepLink().app || RELEASE_APPS[0].key);
    const [rawQuery, setRawQuery] = useState('');
    // The version the reader last picked, kept per app, so switching to the other app and back does
    // not throw away the note they were reading.
    const [requestedByApp, setRequestedByApp] = useState({});

    const app = findApp(appKey);
    const query = normaliseQuery(rawQuery);
    const scored = useMemo(() => scoreNotes(app.notes, query), [app, query]);

    // Derived, never stored: a query can filter the picked version away, and storing the correction
    // would overwrite what the reader chose the moment they clear the search again.
    const selected = resolveSelectedVersion(scored, requestedByApp[appKey] || null);
    const note = (scored.find(s => s.note.version === selected) || {}).note || null;
    const newest = app.notes[0] ? app.notes[0].version : null;

    function selectApp(key) {
        setAppKey(key);
        writeDeepLink({ tab: 'release-notes', app: key });
    }

    function pickVersion(version) {
        setRequestedByApp(prev => ({ ...prev, [appKey]: version }));
    }

    return (
        <div className="release-notes">
            {/* The toolbar row and the surface card around it are the Non Billable Caps and
                User Management shape: one padded row with a bottom border, then the body. */}
            <div className="release-notes-toolbar">
                <div className="release-notes-apps" role="group" aria-label="Release notes for">
                    {RELEASE_APPS.map(entry => (
                        <button
                            key={entry.key}
                            type="button"
                            className={`btn btn-sm${entry.key === appKey ? ' btn-primary' : ' btn-ghost'}`}
                            aria-pressed={entry.key === appKey}
                            onClick={() => selectApp(entry.key)}
                        >
                            {entry.label}
                        </button>
                    ))}
                </div>

                <div className="search-box">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                    {/* type="text", not type="search": a search input draws the browser's own clear
                        cross, which would sit beside ours and clear the field twice over. */}
                    <input
                        type="text"
                        className="search-input"
                        value={rawQuery}
                        onChange={e => setRawQuery(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Escape') setRawQuery(''); }}
                        placeholder={SEARCH_LABEL}
                        aria-label={SEARCH_LABEL}
                    />
                    {rawQuery !== '' && (
                        <button
                            type="button"
                            className="release-notes-clear"
                            aria-label="Clear the search"
                            onClick={() => setRawQuery('')}
                        >
                            &times;
                        </button>
                    )}
                </div>
            </div>

            {/* The search result in words, for a screen reader. Polite, so it is announced once the
                reader stops typing rather than on every keystroke. Worded differently from the
                visible sentence below, which would otherwise be read out twice. */}
            <div className="release-notes-content">
            <p aria-live="polite" className="sr-only">
                {query === ''
                    ? ''
                    : scored.length === 0
                        ? 'No release matches what you searched for'
                        : `${scored.length} release${scored.length === 1 ? '' : 's'} match what you searched for`}
            </p>

            {/* Nothing matched, so there is no list to pick from and no note to read. One sentence,
                not an empty rail beside an empty body. */}
            {scored.length === 0 && (
                <div className="empty-state">
                    <p>No release notes for {app.label} contain "{query}".</p>
                </div>
            )}

            {scored.length > 0 && (
                <>
                    {/* Phone: the rail would eat half the width, so the same list is a picklist. */}
                    <div className="release-notes-picklist">
                        <select
                            value={selected || ''}
                            onChange={e => pickVersion(e.target.value)}
                            aria-label="Select a version"
                            className="release-notes-select"
                        >
                            {scored.map(({ note: n, matches }) => (
                                <option key={n.version} value={n.version}>
                                    {/* A short month here, unlike the rail: a native select cannot
                                        wrap or shrink its own text. */}
                                    {`${n.version} - ${formatReleaseDate(n.date, 'short')}`}
                                    {n.version === newest && query === '' ? ' (nyeste)' : ''}
                                    {query === '' ? '' : ` - ${matchLabel(matches)}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="release-notes-layout">
                        <nav className="release-notes-rail" aria-label="Select a version">
                            {scored.map(({ note: n, matches }) => (
                                <button
                                    key={n.version}
                                    type="button"
                                    className={`release-notes-rail-item${n.version === selected ? ' release-notes-rail-item--active' : ''}`}
                                    aria-current={n.version === selected}
                                    onClick={() => pickVersion(n.version)}
                                >
                                    <span className="release-notes-rail-version">
                                        {n.version}
                                        {n.version === newest && query === '' && (
                                            <span className="release-notes-badge">Nyeste</span>
                                        )}
                                    </span>
                                    <span className="release-notes-rail-date">
                                        {formatReleaseDate(n.date)}
                                        {query === '' ? '' : ` - ${matchLabel(matches)}`}
                                    </span>
                                </button>
                            ))}
                        </nav>

                        <div className="release-notes-detail">
                            {note !== null && (
                                <article>
                                    <header className="release-notes-header">
                                        <h3>Version {note.version}</h3>
                                        <span className="release-notes-date">{formatReleaseDate(note.date)}</span>
                                        {note.version === newest && <span className="release-notes-badge">Nyeste</span>}
                                        {query !== '' && (
                                            <span className="release-notes-date">
                                                {matchLabel(countMatches(note.body, query))}
                                            </span>
                                        )}
                                    </header>
                                    {note.tags.length > 0 && (
                                        <ul className="release-notes-tags">
                                            {note.tags.map(tag => (
                                                <li
                                                    key={tag}
                                                    className={`release-notes-tag release-notes-tag--${tag.toLowerCase()}`}
                                                >
                                                    {tag}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <Markdown source={note.body} query={query} />
                                </article>
                            )}
                        </div>
                    </div>
                </>
            )}
            </div>
        </div>
    );
}
